import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (user: User, token: string, apiKey: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
  apiKey: string | null;
  updateApiKey: (newApiKey: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);

  useEffect(() => {
    // Load auth data from localStorage on app start
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const savedApiKey = localStorage.getItem('apiKey');

    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      setApiKey(savedApiKey);
    }
  }, []);

  const login = (userData: User, userToken: string, userApiKey: string) => {
    setUser(userData);
    setToken(userToken);
    setApiKey(userApiKey);
    
    localStorage.setItem('token', userToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('apiKey', userApiKey);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setApiKey(null);
    
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('apiKey');
  };

  const updateApiKey = (newApiKey: string) => {
    setApiKey(newApiKey);
    localStorage.setItem('apiKey', newApiKey);
  };

  const value = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!user && !!token,
    apiKey,
    updateApiKey,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
